import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, RefreshCcw, Search, Shield, Trash2, UserPlus, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EmptyState } from '@/components/common/empty-state';
import { PageHeader } from '@/components/common/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import {
  createOrgInvitation,
  getOrgProfile,
  listMembers,
  listOrgInvitations,
  listRoles,
  removeMember,
  resendOrgInvitation,
  revokeOrgInvitation,
  updateMember,
  updateOrgProfile,
  type MemberRow,
  type OrgInvitationRow,
  type RoleRow,
} from '@/lib/org-api';
import { initialsFrom } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

type Tab = 'general' | 'members' | 'roles' | 'invitations';

export function OrgSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const membership = user?.memberships.find((m) => m.organizationId === activeOrgId) ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab | null) ?? 'general';

  const setTab = (next: Tab) => {
    searchParams.set('tab', next);
    setSearchParams(searchParams, { replace: true });
  };

  const canManage = Boolean(
    user?.isSuperAdmin ||
    membership?.permissions.includes('org.manage_settings') ||
    membership?.permissions.includes('org.manage_roles') ||
    membership?.permissions.includes('org.invite_users'),
  );

  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      <PageHeader
        title="Organization settings"
        description="General info, members, roles, and invitations."
      />

      <div className="mb-6 flex flex-wrap gap-1 border-b">
        {(
          [
            ['general', 'General'],
            ['members', 'Members'],
            ['roles', 'Roles & Permissions'],
            ['invitations', 'Invitations'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={[
              'rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {!activeOrgId ? (
        <EmptyState
          title="No active organization"
          description="Use the org switcher at the top to pick an organization."
        />
      ) : tab === 'general' ? (
        <GeneralTab orgId={activeOrgId} canEdit={canManage} />
      ) : tab === 'members' ? (
        <MembersTab orgId={activeOrgId} canEdit={canManage} />
      ) : tab === 'roles' ? (
        <RolesTab orgId={activeOrgId} />
      ) : (
        <InvitationsTab orgId={activeOrgId} canInvite={canManage} />
      )}
    </div>
  );
}

// -------------------- General tab --------------------

function GeneralTab({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const { success, error } = useToast();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['org', orgId, 'profile'],
    queryFn: () => getOrgProfile(orgId),
  });

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    if (query.data) {
      setName(query.data.name);
      setTimezone(query.data.timezone);
      setLogoUrl(query.data.logoUrl ?? '');
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () =>
      updateOrgProfile(orgId, {
        name,
        timezone,
        logoUrl: logoUrl.trim() === '' ? null : logoUrl.trim(),
      }),
    onSuccess: () => {
      success('Profile updated');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'profile'] });
    },
    onError: (e) => error('Could not save', extractApiErrorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canEdit || mutation.isPending) return;
    mutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>General</CardTitle>
        <CardDescription>Organization profile. The slug is immutable.</CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : query.isError ? (
          <EmptyState
            title="Could not load organization"
            description={extractApiErrorMessage(query.error)}
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="org-name">Name</Label>
                <Input
                  id="org-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-slug-display">Slug</Label>
                <Input id="org-slug-display" value={query.data?.slug ?? ''} readOnly disabled />
                <p className="text-xs text-muted-foreground">Slugs are immutable.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-tz">Timezone</Label>
                <Input
                  id="org-tz"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  disabled={!canEdit}
                  placeholder="UTC"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-logo">Logo URL</Label>
                <Input
                  id="org-logo"
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  disabled={!canEdit}
                  placeholder="https://cdn.example.com/logo.png"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={!canEdit || mutation.isPending}>
                {mutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
              {query.data ? (
                <span className="text-xs text-muted-foreground">
                  Created {new Date(query.data.createdAt).toLocaleDateString()}
                </span>
              ) : null}
            </div>
            {!canEdit ? (
              <p className="text-xs text-muted-foreground">
                Only organization administrators can edit the profile.
              </p>
            ) : null}
          </form>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------- Members tab --------------------

const MEMBERS_PAGE_SIZE = 20;

function MembersTab({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'SUSPENDED'>('ALL');
  const [page, setPage] = useState(0);
  const [confirmRemove, setConfirmRemove] = useState<MemberRow | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  const rolesQuery = useQuery({
    queryKey: ['org', orgId, 'roles'],
    queryFn: () => listRoles(orgId),
  });

  const query = useQuery({
    queryKey: ['org', orgId, 'members', { debounced, roleId, status, page }],
    queryFn: () =>
      listMembers(orgId, {
        ...(debounced ? { search: debounced } : {}),
        ...(roleId ? { roleId } : {}),
        ...(status !== 'ALL' ? { status } : {}),
        limit: MEMBERS_PAGE_SIZE,
        offset: page * MEMBERS_PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const updateRole = useMutation({
    mutationFn: ({ userId, newRoleId }: { userId: string; newRoleId: string }) =>
      updateMember(orgId, userId, { roleId: newRoleId }),
    onSuccess: () => {
      success('Member role updated');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
    },
    onError: (e) => error('Role change failed', extractApiErrorMessage(e)),
  });

  const toggleStatus = useMutation({
    mutationFn: ({ userId, nextStatus }: { userId: string; nextStatus: 'ACTIVE' | 'SUSPENDED' }) =>
      updateMember(orgId, userId, { status: nextStatus }),
    onSuccess: (_r, vars) => {
      success(vars.nextStatus === 'ACTIVE' ? 'Member reactivated' : 'Member suspended');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
    },
    onError: (e) => error('Status change failed', extractApiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => removeMember(orgId, userId),
    onSuccess: (res) => {
      success(
        'Member removed',
        res.reassignedIssueCount > 0
          ? `Reassigned ${res.reassignedIssueCount} issue${res.reassignedIssueCount === 1 ? '' : 's'} to you`
          : undefined,
      );
      qc.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
      setConfirmRemove(null);
    },
    onError: (e) => error('Remove failed', extractApiErrorMessage(e)),
  });

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / MEMBERS_PAGE_SIZE));
  const empty = !query.isLoading && (query.data?.items.length ?? 0) === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            Manage everyone in this organization. Removing a member reassigns their issues to you.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label
              htmlFor="member-search"
              className="mb-1 block text-xs uppercase text-muted-foreground"
            >
              Search
            </Label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="member-search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                className="pl-9"
                placeholder="Search by name or email"
              />
            </div>
          </div>
          <div>
            <Label
              htmlFor="member-role"
              className="mb-1 block text-xs uppercase text-muted-foreground"
            >
              Role
            </Label>
            <Select
              id="member-role"
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All roles</option>
              {rolesQuery.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label
              htmlFor="member-status"
              className="mb-1 block text-xs uppercase text-muted-foreground"
            >
              Status
            </Label>
            <Select
              id="member-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as typeof status);
                setPage(0);
              }}
            >
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
            </Select>
          </div>
        </div>

        {query.isError ? (
          <EmptyState
            title="Could not load members"
            description={extractApiErrorMessage(query.error)}
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        ) : empty ? (
          <EmptyState
            icon={UserPlus}
            title="No members yet"
            description="Invite teammates from the Invitations tab."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={`sk-${i}-${j}`}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : query.data?.items.map((m) => (
                    <TableRow key={m.userId}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="grid h-8 w-8 place-items-center rounded-full bg-muted text-xs font-semibold">
                            {initialsFrom(m.name, m.email)}
                          </div>
                          <span className="font-medium">{m.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{m.email}</TableCell>
                      <TableCell>
                        {canEdit ? (
                          <Select
                            value={m.roleId}
                            onChange={(e) =>
                              updateRole.mutate({ userId: m.userId, newRoleId: e.target.value })
                            }
                            className="h-8 text-xs"
                            aria-label={`Role for ${m.name}`}
                            disabled={updateRole.isPending}
                          >
                            {rolesQuery.data?.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          <span>{m.roleName}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {m.status === 'ACTIVE' ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="warning">Suspended</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(m.joinedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canEdit || toggleStatus.isPending}
                            onClick={() =>
                              toggleStatus.mutate({
                                userId: m.userId,
                                nextStatus: m.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
                              })
                            }
                          >
                            {m.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={!canEdit}
                            onClick={() => setConfirmRemove(m)}
                          >
                            <Trash2 className="mr-1 h-3 w-3" aria-hidden /> Remove
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}

        {total > MEMBERS_PAGE_SIZE ? (
          <div className="flex items-center justify-between pt-2 text-sm text-muted-foreground">
            <span>
              Page {page + 1} of {totalPages} · {total} total
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>

      <ConfirmDialog
        open={Boolean(confirmRemove)}
        title={`Remove ${confirmRemove?.name ?? ''}?`}
        description="Their project memberships will be removed and issues they created or reported get reassigned to you."
        confirmLabel="Remove member"
        destructive
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          if (!confirmRemove) return;
          await remove.mutateAsync(confirmRemove.userId);
        }}
      />
    </Card>
  );
}

// -------------------- Roles tab --------------------

function RolesTab({ orgId }: { orgId: string }) {
  const query = useQuery({
    queryKey: ['org', orgId, 'roles'],
    queryFn: () => listRoles(orgId),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Roles & Permissions</CardTitle>
        <CardDescription>
          Each organization ships with four default roles. Members hold one role per org.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <EmptyState
            title="Could not load roles"
            description={extractApiErrorMessage(query.error)}
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {query.data?.map((role: RoleRow) => (
              <Card key={role.id}>
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-muted-foreground" aria-hidden />
                        <span className="font-medium">{role.name}</span>
                        {role.isDefault ? <Badge variant="outline">Default</Badge> : null}
                      </div>
                      {role.description ? (
                        <p className="text-xs text-muted-foreground">{role.description}</p>
                      ) : null}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {role.memberCount} member{role.memberCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {role.permissions.length === 0 ? (
                      <span className="text-xs text-muted-foreground">No permissions</span>
                    ) : (
                      role.permissions.map((p) => (
                        <Badge key={p} variant="secondary" className="font-mono text-[10px]">
                          {p}
                        </Badge>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------- Invitations tab --------------------

const INVITATIONS_PAGE_SIZE = 20;

function InvitationsTab({ orgId, canInvite }: { orgId: string; canInvite: boolean }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [status, setStatus] = useState<'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED' | 'ALL'>(
    'PENDING',
  );
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<OrgInvitationRow | null>(null);

  const rolesQuery = useQuery({
    queryKey: ['org', orgId, 'roles'],
    queryFn: () => listRoles(orgId),
  });

  const invQuery = useQuery({
    queryKey: ['org', orgId, 'invitations', { status, page }],
    queryFn: () =>
      listOrgInvitations(orgId, {
        ...(status !== 'ALL' ? { status } : {}),
        limit: INVITATIONS_PAGE_SIZE,
        offset: page * INVITATIONS_PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const resend = useMutation({
    mutationFn: (id: string) => resendOrgInvitation(orgId, id),
    onSuccess: () => {
      success('Invitation resent');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'invitations'] });
    },
    onError: (e) => error('Resend failed', extractApiErrorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeOrgInvitation(orgId, id),
    onSuccess: () => {
      success('Invitation revoked');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'invitations'] });
      setConfirmRevoke(null);
    },
    onError: (e) => error('Revoke failed', extractApiErrorMessage(e)),
  });

  const total = invQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / INVITATIONS_PAGE_SIZE));
  const empty = !invQuery.isLoading && (invQuery.data?.items.length ?? 0) === 0;

  const nonAdminRoles = useMemo(
    () => rolesQuery.data?.filter((r) => r.templateKey !== 'ORG_ADMIN' || r.isDefault) ?? [],
    [rolesQuery.data],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Invitations</CardTitle>
          <CardDescription>Pending and historical invites for this organization.</CardDescription>
        </div>
        <Button onClick={() => setCreateOpen(true)} disabled={!canInvite}>
          <Plus className="mr-1 h-4 w-4" aria-hidden /> Invite member
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="w-48">
          <Label
            htmlFor="org-inv-status"
            className="mb-1 block text-xs uppercase text-muted-foreground"
          >
            Status
          </Label>
          <Select
            id="org-inv-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setPage(0);
            }}
          >
            <option value="ALL">All</option>
            <option value="PENDING">Pending</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="EXPIRED">Expired</option>
            <option value="REVOKED">Revoked</option>
          </Select>
        </div>

        {invQuery.isError ? (
          <EmptyState
            title="Could not load invitations"
            description={extractApiErrorMessage(invQuery.error)}
            action={<Button onClick={() => invQuery.refetch()}>Retry</Button>}
          />
        ) : empty ? (
          <EmptyState
            icon={Mail}
            title="No invitations yet"
            description={
              canInvite
                ? 'Invite your first teammate.'
                : 'Ask an administrator to issue invitations.'
            }
            action={
              canInvite ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-1 h-4 w-4" aria-hidden /> Invite member
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invited by</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invQuery.isLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={`sk-${i}-${j}`}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : invQuery.data?.items.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-mono text-xs">{inv.email}</TableCell>
                      <TableCell>{inv.roleName}</TableCell>
                      <TableCell>
                        <StatusBadge status={inv.status} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {inv.invitedByName}
                      </TableCell>
                      <TableCell className="tabular-nums">{inv.attemptCount}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(inv.expiresAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canInvite || inv.status === 'ACCEPTED'}
                            onClick={() => resend.mutate(inv.id)}
                          >
                            <RefreshCcw className="mr-1 h-3 w-3" aria-hidden /> Resend
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={!canInvite || inv.status !== 'PENDING'}
                            onClick={() => setConfirmRevoke(inv)}
                          >
                            <XCircle className="mr-1 h-3 w-3" aria-hidden /> Revoke
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}

        {total > INVITATIONS_PAGE_SIZE ? (
          <div className="flex items-center justify-between pt-2 text-sm text-muted-foreground">
            <span>
              Page {page + 1} of {totalPages} · {total} total
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>

      <CreateInvitationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={orgId}
        roles={nonAdminRoles}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['org', orgId, 'invitations'] });
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmRevoke)}
        title={`Revoke invitation for ${confirmRevoke?.email ?? ''}?`}
        description="The current link will stop working immediately. You can resend a new invitation later."
        confirmLabel="Revoke"
        destructive
        onCancel={() => setConfirmRevoke(null)}
        onConfirm={() => {
          if (!confirmRevoke) return Promise.resolve();
          return revoke.mutateAsync(confirmRevoke.id);
        }}
      />
    </Card>
  );
}

function CreateInvitationDialog({
  open,
  onOpenChange,
  orgId,
  roles,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  orgId: string;
  roles: RoleRow[];
  onCreated: () => void;
}) {
  const { success, error } = useToast();
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setRoleId('');
      setSubmitting(false);
    } else if (roles.length > 0 && !roleId) {
      const preferred = roles.find((r) => r.templateKey === 'MEMBER') ?? roles[0];
      if (preferred) setRoleId(preferred.id);
    }
  }, [open, roles, roleId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting || !roleId) return;
    setSubmitting(true);
    try {
      await createOrgInvitation(orgId, { email: email.trim().toLowerCase(), roleId });
      success('Invitation sent', `Emailed ${email}`);
      onCreated();
      onOpenChange(false);
    } catch (err) {
      error('Invitation failed', extractApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite member</DialogTitle>
          <DialogDescription>
            They&apos;ll receive an email with a secure link to accept. Any pending invitation for
            this email is replaced.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit} noValidate>
          <div className="space-y-2">
            <Label htmlFor="inv-email">Email</Label>
            <Input
              id="inv-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inv-role">Role</Label>
            <Select
              id="inv-role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              required
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !roleId}>
              {submitting ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }: { status: OrgInvitationRow['status'] }) {
  switch (status) {
    case 'PENDING':
      return <Badge variant="warning">Pending</Badge>;
    case 'ACCEPTED':
      return <Badge variant="success">Accepted</Badge>;
    case 'EXPIRED':
      return <Badge variant="outline">Expired</Badge>;
    case 'REVOKED':
      return <Badge variant="destructive">Revoked</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}
